import fs from "node:fs/promises";
import path from "node:path";

import mongoose from "mongoose";

import { type FileType } from "../models/constants/model-constants";
import { Media } from "../models/media";
import { logger } from "../utils/logger";

// File upload result interface
export interface FileUploadResult {
  mediaId: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
}

// File service class
class FileService {
  private baseUrl: string;
  private uploadDir: string;

  constructor() {
    this.baseUrl = process.env.IMAGE_URL || "http://localhost:3080/uploads";
    this.uploadDir = path.join(process.cwd(), "uploads");
  }

  /**
   * Create a Media record from an uploaded file
   */
  async createMediaRecord(
    file: Express.Multer.File,
    userId: string | mongoose.Types.ObjectId,
    fileType: FileType,
    options?: {
      isPublic?: boolean;
      isTemporary?: boolean;
      tags?: string[];
      description?: string;
    },
  ): Promise<FileUploadResult> {
    try {
      const media = new Media({
        userId: new mongoose.Types.ObjectId(userId),
        fileName: file.filename,
        originalName: file.originalname,
        fileType,
        mimeType: file.mimetype,
        fileSize: file.size,
        isPublic: options?.isPublic ?? false,
        isTemporary: options?.isTemporary ?? false,
        uploadedBy: new mongoose.Types.ObjectId(userId),
        tags: options?.tags,
        description: options?.description,
      });

      await media.save();

      const mediaId = String(media._id);
      logger.info(`Media record created: ${mediaId} for file ${file.filename}`);

      // Construct file URL
      const fileUrl = `${this.baseUrl}/${media.fileName}`;

      return {
        mediaId,
        fileName: media.fileName,
        fileUrl,
        fileSize: media.fileSize,
        mimeType: media.mimeType,
      };
    } catch (error) {
      logger.error("Error creating media record:", error);
      throw error;
    }
  }

  /**
   * Delete a file and its Media record
   */
  async deleteFile(mediaId: string): Promise<void> {
    try {
      const media = await Media.findById(mediaId);

      if (!media) {
        throw new Error("Media record not found");
      }

      // Delete physical file
      const filePath = path.join(this.uploadDir, media.fileName);

      try {
        await fs.unlink(filePath);
        logger.info(`Deleted file: ${filePath}`);
      } catch (error: any) {
        if (error.code !== "ENOENT") {
          logger.error(`Error deleting file ${filePath}:`, error);
        }
      }

      // Delete Media record
      await Media.findByIdAndDelete(mediaId);
      logger.info(`Deleted media record: ${mediaId}`);
    } catch (error) {
      logger.error("Error deleting file:", error);
      throw error;
    }
  }

  /**
   * Delete multiple files
   */
  async deleteFiles(mediaIds: string[]): Promise<void> {
    for (const mediaId of mediaIds) {
      try {
        await this.deleteFile(mediaId);
      } catch (error) {
        logger.error(`Error deleting file ${mediaId}:`, error);
      }
    }
  }

  /**
   * Get file URL from Media record
   */
  async getFileUrl(mediaId: string): Promise<string> {
    const media = await Media.findById(mediaId);

    if (!media) {
      throw new Error("Media record not found");
    }

    // Construct file URL
    return `${this.baseUrl}/${media.fileName}`;
  }

  /**
   * Get Media record by ID
   */
  async getMedia(mediaId: string) {
    return await Media.findById(mediaId);
  }

  /**
   * Cleanup expired temporary files
   */
  async cleanupExpiredFiles(): Promise<number> {
    try {
      const expiredFiles = await Media.findExpiredFiles();

      for (const media of expiredFiles) {
        try {
          const mediaId = String(media._id);
          await this.deleteFile(mediaId);
        } catch (error) {
          logger.error(
            `Error cleaning up expired file ${String(media._id)}:`,
            error,
          );
        }
      }

      logger.info(`Cleaned up ${expiredFiles.length} expired files`);
      return expiredFiles.length;
    } catch (error) {
      logger.error("Error cleaning up expired files:", error);
      throw error;
    }
  }

  /**
   * Delete old file when updating (e.g., replacing profile image)
   */
  async replaceFile(
    oldMediaId: string | undefined,
    newFile: Express.Multer.File,
    userId: string | mongoose.Types.ObjectId,
    fileType: FileType,
    options?: {
      isPublic?: boolean;
      isTemporary?: boolean;
      tags?: string[];
      description?: string;
    },
  ): Promise<FileUploadResult> {
    // Create new media record
    const newMedia = await this.createMediaRecord(
      newFile,
      userId,
      fileType,
      options,
    );

    // Delete old file if exists
    if (oldMediaId) {
      try {
        await this.deleteFile(oldMediaId);
      } catch (error) {
        logger.error(`Error deleting old file ${oldMediaId}:`, error);
      }
    }

    return newMedia;
  }

  /**
   * Get user storage usage
   */
  async getUserStorageUsage(userId: string | mongoose.Types.ObjectId) {
    return await Media.getUserStorageUsage(new mongoose.Types.ObjectId(userId));
  }

  /**
   * Get system storage statistics
   */
  async getSystemStorageStats() {
    return await Media.getSystemStorageStats();
  }
}

// Export singleton instance
export const fileService = new FileService();
